import React, { FC, useCallback } from 'react'
import makeStyles from '@mui/styles/makeStyles';
import {
  EditorState,
} from 'draft-js'

import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'

import ToolbarButtonInline from './ToolbarButtonInline'
import ToolbarButtonBlockData from './ToolbarButtonBlockData'
import ToolbarButtonBlock from './ToolbarButtonBlock'
import ToolbarSelectBlock from './ToolbarSelectBlock'
import ToolbarSelectBlockData from './ToolbarSelectBlockData'
import ToolbarButtonColor from './ToolbarButtonColor'
import ToolbarButtonLink from './ToolbarButtonLink'
import ToolbarButtonMagicTag from './ToolbarButtonMagicTag'
import ToolbarMarketingPrompt from './ToolbarMarketingPrompt'

import {
  ITextEditor_EditorUpdate,
  ITextEditor_Options,
} from '@kaidam/fc-shared/src/types/texteditor'

import {
  TEXT_EDITOR_TEXT_TYPES,
  TEXT_EDITOR_LIST_TYPES,
  TEXT_EDITOR_FONTS,
  TEXT_EDITOR_DEFAULT_FONT,
  TEXT_EDITOR_HORIZONTAL_ALIGN_LEFT,
  TEXT_EDITOR_HORIZONTAL_ALIGN_CENTER,
  TEXT_EDITOR_HORIZONTAL_ALIGN_RIGHT,
  TEXT_EDITOR_HORIZONTAL_ALIGN_DEFAULT,
} from '@kaidam/fc-shared/src/constants'

import {
  ENABLED_MAGIC_TAGS_TEXT_EDITOR,
} from '@kaidam/fc-shared/src/constants'

import icons from '../../icons'

const BoldIcon = icons.bold
const ItalicIcon = icons.italic
const UnderlineIcon = icons.underline
const AlignLeft = icons.alignLeft
const AlignCenter = icons.alignCenter
const AlignRight = icons.alignRight
const ListBulletedIcon = icons.bulletList
const ListNumberedIcon = icons.numerList
const ColorTextIcon = icons.colorText
const LinkIcon = icons.link
const LabelIcon = icons.label

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    height: '100%',
    border: 'none',
    padding: theme.spacing(1),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  button: {
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
  },
  divider: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
}))

interface TextEditorToolbarProps {
  editorState: EditorState,
  options: ITextEditor_Options,
  setEditorState: ITextEditor_EditorUpdate,
}

const TextEditorToolbar: FC<React.PropsWithChildren<TextEditorToolbarProps>> = ({
  editorState,
  options,
  setEditorState,
}) => {
  const {
    horizontalAlign,
    textMagicTags,
    linkMagicTags,
    linkTypes,
    showButtons,
  } = options

  const classes = useStyles()
  const showButton = useCallback((name: string) => {
    if(!showButtons) return true
    return showButtons[name] ? true : false
  }, [
    showButtons,
  ])

  const showDivider = useCallback((names: string[]) => {
    return names.find(name => showButton(name)) ? true : false
  }, [
    showButton,
  ])

  return (
    <Paper elevation={ 0 } square className={ classes.root }>
      {
        showButton('magicTags') && (
          <ToolbarButtonMagicTag
            className={ classes.button }
            icon={ LabelIcon as any }
            enabled={ textMagicTags || ENABLED_MAGIC_TAGS_TEXT_EDITOR }
            addExtraCharacters=" "
            editorState={ editorState }
            setEditorState={ setEditorState }
          >
            <Divider
              className={ classes.divider }
              orientation="vertical"
            />
          </ToolbarButtonMagicTag>
        )
      }
      {
        showButton('bold') && (
          <ToolbarButtonInline
            className={ classes.button }
            title="bold"
            icon={ BoldIcon as any }
            style="BOLD"
            editorState={ editorState }
            setEditorState={ setEditorState }
          />
        )
      }
      {
        showButton('italic') && (
          <ToolbarButtonInline
            className={ classes.button }
            title="italic"
            icon={ ItalicIcon as any }
            style="ITALIC"
            editorState={ editorState }
            setEditorState={ setEditorState }
          />
        )
      }
      {
        showButton('underline') && (
          <ToolbarButtonInline
            className={ classes.button }
            title="underline"
            icon={ UnderlineIcon as any }
            style="UNDERLINE"
            editorState={ editorState }
            setEditorState={ setEditorState }
          />
        )
      }
      {
        showDivider(['magicTags', 'bold', 'italic', 'underline']) && (
          <Divider
            className={ classes.divider }
            orientation="vertical"
          />
        )
      }
      {
        showButton('link') && (
          <ToolbarButtonLink
            title="Edit Link"
            className={ classes.button }
            enabledMagicTags={ linkMagicTags }
            linkTypes={ linkTypes }
            publicFiles={ options.publicFiles }
            sharedFiles={ options.sharedFiles }
            icon={ LinkIcon as any }
            editorState={ editorState }
            setEditorState={ setEditorState }
          />
        )
      }
      {
        showDivider(['link']) && (
          <Divider
            className={ classes.divider }
            orientation="vertical"
          />
        )
      }
      {
        showButton('color') && (
          <ToolbarButtonColor
            className={ classes.button }
            name="color-font"
            icon={ ColorTextIcon as any }
            editorState={ editorState }
            setEditorState={ setEditorState }
          />
        )
      }
      {
        showDivider(['color']) && (
          <Divider
            className={ classes.divider }
            orientation="vertical"
          />
        )
      }
      {
        showButton('align') && (
          <ToolbarButtonBlockData
            className={ classes.button }
            title="left"
            icon={ AlignLeft as any }
            name="align"
            value={ TEXT_EDITOR_HORIZONTAL_ALIGN_LEFT }
            disabledBlocks={ TEXT_EDITOR_LIST_TYPES }
            defaultValue={ horizontalAlign }
            editorState={ editorState }
            setEditorState={ setEditorState }
          />
        )
      }
      {
        showButton('align') && (
          <ToolbarButtonBlockData
            className={ classes.button }
            title="center"
            icon={ AlignCenter as any }
            name="align"
            value={ TEXT_EDITOR_HORIZONTAL_ALIGN_CENTER }
            disabledBlocks={ TEXT_EDITOR_LIST_TYPES }
            defaultValue={ horizontalAlign }
            editorState={ editorState }
            setEditorState={ setEditorState }
          />
        )
      }
      {
        showButton('align') && (
          <ToolbarButtonBlockData
            className={ classes.button }
            title="right"
            icon={ AlignRight as any }
            name="align"
            value={ TEXT_EDITOR_HORIZONTAL_ALIGN_RIGHT }
            disabledBlocks={ TEXT_EDITOR_LIST_TYPES }
            defaultValue={ horizontalAlign }
            editorState={ editorState }
            setEditorState={ setEditorState }
          />
        )
      }
      {
        showDivider(['align']) && (
          <Divider
            className={ classes.divider }
            orientation="vertical"
          />
        )
      }
      {
        showButton('list') && (
          <ToolbarButtonBlock
            className={ classes.button }
            title="bullet list"
            icon={ ListBulletedIcon as any }
            style="unordered-list-item"
            editorState={ editorState }
            setEditorState={ setEditorState }
          />
        )
      }
      {
        showButton('list') && (
          <ToolbarButtonBlock
            className={ classes.button }
            title="numbered list"
            icon={ ListNumberedIcon as any }
            style="ordered-list-item"
            editorState={ editorState }
            setEditorState={ setEditorState }
          />
        )
      }
      {
        showDivider(['list']) && (
          <Divider
            className={ classes.divider }
            orientation="vertical"
          />
        )
      }
      {
        showButton('style') && (
          <ToolbarSelectBlock
            types={ TEXT_EDITOR_TEXT_TYPES }
            defaultValue="unstyled"
            editorState={ editorState }
            setEditorState={ setEditorState }
          />
        )
      }
      {
        showDivider(['style']) && (
          <Divider
            className={ classes.divider }
            orientation="vertical"
          />
        )
      }
      {
        showButton('font') && (
          <ToolbarSelectBlockData
            options={ TEXT_EDITOR_FONTS }
            name="font"
            defaultValue={ TEXT_EDITOR_DEFAULT_FONT }
            editorState={ editorState }
            setEditorState={ setEditorState }
          />
        )
      }
      {
        showDivider(['font']) && (
          <Divider
            className={ classes.divider }
            orientation="vertical"
          />
        )
      }
      {
        showButton('marketingPrompts') && options.onRunMarketingPrompt && (
          <ToolbarMarketingPrompt
            prompts={ options.marketingPrompts || [] }
            onRunPrompt={ options.onRunMarketingPrompt }
            editorState={ editorState }
            setEditorState={ setEditorState }
          />
        )
      }
      {
        showDivider(['marketingPrompts']) && (
          <Divider
            className={ classes.divider }
            orientation="vertical"
          />
        )
      }
    </Paper>
  )
}

export default TextEditorToolbar